import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button'
import Balaclava from 'cityads-v3/components/Balaclava';
import LoginIframe from 'cityads-v3/containers/Auth/LoginIframe';
import SetResponseCode from 'cityads-v3/components/Router/SetResponseCode';
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Helmet } from 'react-helmet';

import { HTTP_STATUS_FORBIDDEN } from 'cityads-v3/utils/apiUtils';
import { ensureArray } from 'cityads-v3/utils/arrayUtils';

import classes from './AuthorizationRequired.scss'

export default function AuthorizationRequired({ children, ...rest }) {
  const isCheckingAuth = useSelector(state => state.auth.checkingAuth);
  const isAuthChecked = useSelector(state => state.auth.isAuthChecked);
  const isStorageCookieRequesting = useSelector(state => state.auth.isStorageCookieRequesting);
  const isStorageCookieChecked = useSelector(state => state.auth.isStorageCookieChecked);
  const userRoleCode = useSelector(state => state.auth.user?.user_role_code);

  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);

  const { t } = useTranslation(['main']);

  const isAuthPending = isCheckingAuth || !isAuthChecked || isStorageCookieRequesting || !isStorageCookieChecked

  return (
    <AuthorizationRequiredInternal
      {...rest}
      isAuthPending = {isAuthPending}
      isLoggedIn    = {isLoggedIn}
      userRoleCode  = {userRoleCode}
      t             = {t}
    >
      {children}
    </AuthorizationRequiredInternal>
  );
}

AuthorizationRequired.propTypes = {
  allowedUserRoleCode: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

function AuthorizationRequiredInternal({ children, allowedUserRoleCode, isAuthPending, isLoggedIn, userRoleCode, t, ...rest }) {
  const exitButton = useSelector(state => state.auth.user?.headerBar?.filter(item => item.type === 'exit')[0])

  if (isAuthPending) {
    return (
      <div className={classes.container}>
        <CircularProgress size={40} />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Helmet>
          <title>{t('enter')}</title>
        </Helmet>

        <Balaclava
          title = {t('enter')}
        />

        <div className={classes.container}>
          <LoginIframe className={classes.frame} />
        </div>
      </>
    );
  }

  if (allowedUserRoleCode && ensureArray(allowedUserRoleCode).indexOf(userRoleCode) < 0) {
    return (
      <>
        <SetResponseCode code={HTTP_STATUS_FORBIDDEN} noOverride />

        <Helmet>
          <title>{t('accessDenied')}</title>
        </Helmet>

        <Balaclava
          title = {t('accessDenied')}
        />

        <div className={classes.container}>
          <div className={classes.forbidden}>
            <Typography align="center" color="error">
              <LockOutlinedIcon fontSize="large" />
            </Typography>
            <Typography align="center" color="error" variant="h3" paragraph>
              {t('accessDenied')}
            </Typography>
            <Typography align="center" color="error" paragraph>
              {t('youHaveNoRights')}
            </Typography>

            <div className={classes.buttons}>
              <Button
                to="/"
                component={Link}
              >
                {t('page404.gohome')}
              </Button>
              {exitButton ? (
                <Button
                  classes={{ root: classes.exitButton }}
                  color="secondary"
                  href={exitButton.data.url}
                >
                  {exitButton.data.label}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </>
    );
  }

  return children;
}
